// Shared date/time utilities for schedule components

export const DAY_NAMES = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
export const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

/** Returns the Monday of the week containing `date` at 00:00:00 local time. */
export const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Returns array of 7 Date objects Mon–Sun for the week containing weekStart. */
export const getWeekDays = (weekStart) =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

/** "YYYY-MM-DD" from a Date */
export const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** "Mon Apr 28" display format */
export const formatDayLabel = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/** "9:00 AM" from a Firestore Timestamp or Date */
export const formatTime = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/** "9:00 AM – 5:00 PM" */
export const formatTimeRange = (startTs, endTs) =>
  `${formatTime(startTs)} – ${formatTime(endTs)}`;

/** Converts "HH:MM" + "YYYY-MM-DD" → Date */
export const timeStrToDate = (dateStr, timeStr) => {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  return new Date(y, mo - 1, d, h, mi, 0, 0);
};

/** Converts Date → "HH:MM" */
export const dateToTimeStr = (date) => {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

/** "Apr 27 – May 3, 2026" range label */
export const formatWeekRange = (weekStart) => {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  const start = weekStart.toLocaleDateString('en-US', opts);
  const end   = weekEnd.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${start} – ${end}`;
};

// ─── Role & zone colors ───────────────────────────────────────────────────────

export const ROLE_COLORS = {
  Server:     'bg-blue-50 border-blue-300 text-blue-800',
  Host:       'bg-emerald-50 border-emerald-300 text-emerald-800',
  Cook:       'bg-orange-50 border-orange-300 text-orange-800',
  Bartender:  'bg-purple-50 border-purple-300 text-purple-800',
  Dishwasher: 'bg-stone-50 border-stone-300 text-stone-700',
  Expo:       'bg-yellow-50 border-yellow-300 text-yellow-800',
  Manager:    'bg-red-50 border-red-300 text-red-800',
  Owner:      'bg-rose-50 border-rose-300 text-rose-800',
};
export const DEFAULT_ROLE_COLOR = 'bg-neutral-50 border-neutral-300 text-neutral-700';

export const ZONE_COLORS = {
  FOH: 'bg-sky-700 border-sky-800 text-white',
  BOH: 'bg-stone-700 border-stone-800 text-white',
  Bar: 'bg-violet-700 border-violet-800 text-white',
};
export const DEFAULT_ZONE_COLOR = 'bg-neutral-700 border-neutral-800 text-white';

// Dark background colors for numeric floor zones (text always white). Cycles if zone # exceeds array length.
export const FLOOR_ZONE_COLORS = [
  '#1d4ed8', // blue-700
  '#15803d', // green-700
  '#b45309', // amber-700
  '#7c3aed', // violet-700
  '#be185d', // pink-700
  '#0f766e', // teal-700
  '#c2410c', // orange-700
  '#3730a3', // indigo-700
];

export const UNZONED_COLOR = 'bg-neutral-100 border-neutral-300 text-neutral-400';

export const getShiftColor = (shift, colorMode) => {
  if (colorMode === 'zone') {
    if (shift.zone) return ZONE_COLORS[shift.zone] || DEFAULT_ZONE_COLOR;
    return UNZONED_COLOR;
  }
  return ROLE_COLORS[shift.role] || DEFAULT_ROLE_COLOR;
};

export const COMMON_ROLES = [
  'Server', 'Host', 'Cook', 'Bartender', 'Dishwasher', 'Expo', 'Manager', 'Owner',
];
export const COMMON_ZONES = ['FOH', 'BOH', 'Bar'];
